import axios from "axios";
import { environment } from "../../environments/environment";
import { Api } from "./api-config";
import { URLS } from "./app-urls";

export type HubStatus = {
  /** undefined when talking to a backend that predates the field. */
  hub_configured?: boolean;
  hub_online: boolean;
  local_fallback_available: boolean;
};

/** The Access Hub's login page URL (URL_LOGIN_HUB), read from Settings --
 * null if unset or the request itself fails. Callers decide what "no URL"
 * means for them (redirect target, origin check, an informational link,
 * ...), so this only centralizes the fetch and response shape, not the
 * fallback behavior. */
export const fetchHubLoginUrl = async (): Promise<string | null> => {
  try {
    const response = await Api.get(`${URLS.SETTINGS}get_url_login/`);
    const value = response?.data?.[0]?.value?.trim();
    return value || null;
  } catch {
    return null;
  }
};

/** hub_online / local_fallback_available, or null if the request itself
 * failed -- distinct from hub_online: false, which is a real answer. */
export const fetchHubStatus = async (): Promise<HubStatus | null> => {
  try {
    const response = await Api.get(URLS.HUB_STATUS);
    const configured = response?.data?.hub_configured;
    return {
      hub_configured: typeof configured === "boolean" ? configured : undefined,
      hub_online: !!response?.data?.hub_online,
      local_fallback_available: !!response?.data?.local_fallback_available,
    };
  } catch {
    return null;
  }
};

/** Asks the backend to validate `token`, which it renews on the spot if it
 * is expired but still renewable. Returns the token to keep using (the
 * renewed one from `new_access_token`, or `token` itself if it was still
 * good), or null if the session really is gone.
 *
 * Deliberately bypasses `Api`: its interceptors clear the stored token on a
 * 401 and leave the promise pending forever on a 470, and the caller here
 * needs a plain answer either way. */
export const renewSession = async (token: string): Promise<string | null> => {
  try {
    const response = await axios.get(`${environment.baseURL ?? ""}${URLS.AUTH}session/`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.headers?.["new_access_token"] || token;
  } catch {
    return null;
  }
};
